import { html, type TemplateResult } from 'lit';
import { type FieldErrors as FormFieldErrors, firstFieldError as firstFormFieldError, clearFormFieldError } from '@src/modules/kernel/FieldErrors.ts';

export type { FormFieldErrors };
export { firstFormFieldError, clearFormFieldError };

const inputBase =
  'md-field__input input-md w-full min-h-[2.75rem] rounded-[var(--md-shape-corner-medium)] border px-4 py-2.5 text-base outline-none transition-[border-color,box-shadow,background-color] duration-[var(--md-motion-duration-short2)] ease-[cubic-bezier(0.2,0,0,1)]';

function shellFieldLabel(label: string, id: string, required = true): TemplateResult {
  return html`
    <label class="md-field__label" for=${id}>
      ${label}${required ? html`<span class="md-field__required" aria-hidden="true"> *</span>` : ''}
    </label>
  `;
}

function shellBlockLabel(label: string, required = true): TemplateResult {
  return html`
    <p class="md-field__label">
      ${label}${required ? html`<span class="md-field__required" aria-hidden="true"> *</span>` : ''}
    </p>
  `;
}

function shellFieldInputClass(invalid: boolean, extra = ''): string {
  return `${inputBase}${invalid ? ' md-field__input--invalid' : ''}${extra ? ` ${extra}` : ''}`;
}

function shellFieldHint(fieldId: string, error?: string): TemplateResult {
  if (!error) return html``;
  return html`<p id="${fieldId}-error" class="md-field__hint md-field__hint--error" role="alert">${error}</p>`;
}

export function shellFormErrorSummary(errors: FormFieldErrors): TemplateResult {
  const items = Object.entries(errors).filter(([, msg]) => msg);
  if (items.length === 0) return html``;
  return html`
    <div class="md-form-error-summary md-error-banner rounded-md px-4 py-3 text-sm" role="alert" tabindex="-1">
      <p class="font-medium" style="color: var(--md-sys-color-on-error-container);">Revise los campos marcados:</p>
      <ul class="mt-2 list-disc space-y-1 pl-5" style="color: var(--md-sys-color-on-error-container);">
        ${items.map(
          ([fieldId, msg]) => html`
            <li>
              <a class="underline underline-offset-2" href="#${fieldId}" @click=${(e: Event) => {
                e.preventDefault();
                const el = document.getElementById(fieldId);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (el instanceof HTMLElement) el.focus({ preventScroll: true });
              }}>${msg}</a>
            </li>
          `,
        )}
      </ul>
    </div>
  `;
}

export function shellSelectField(opts: {
  label: string;
  id: string;
  value: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  options: TemplateResult;
}): TemplateResult {
  const invalid = Boolean(opts.error);
  const required = opts.required ?? true;
  return html`
    <div class="md-field md-field--stacked ${invalid ? 'md-field--invalid' : ''}">
      ${shellFieldLabel(opts.label, opts.id, required)}
      <select
        id=${opts.id}
        name=${opts.id}
        class=${`${shellFieldInputClass(invalid)} bg-[color:var(--md-sys-color-surface-container-highest)]`}
        ?required=${required}
        ?disabled=${opts.disabled ?? false}
        aria-invalid=${invalid ? 'true' : 'false'}
        aria-describedby=${invalid ? `${opts.id}-error` : undefined}
        .value=${opts.value}
        @change=${(e: Event) => opts.onChange((e.target as HTMLSelectElement).value)}
      >
        ${opts.options}
      </select>
      ${shellFieldHint(opts.id, opts.error)}
    </div>
  `;
}

export function shellCatalogSelectOptions(
  items: ReadonlyArray<{ publicId: string; nombre: string }>,
  selectedPublicId: string,
): TemplateResult {
  return html`
    <option value="">— Seleccione —</option>
    ${items.map(
      (item) => html`<option value=${item.publicId} ?selected=${item.publicId === selectedPublicId}>${item.nombre}</option>`,
    )}
  `;
}

export function addSelectChipValue(selected: string[], value: string): string[] {
  if (!value || selected.includes(value)) return selected;
  return [...selected, value];
}

export function removeSelectChipValue(selected: string[], value: string): string[] {
  return selected.filter((id) => id !== value);
}

export function selectChipsSelectionSummary(
  labels: string[],
  noneLabel: string,
  manyLabel: (count: number) => string,
): string {
  if (labels.length === 0) return noneLabel;
  if (labels.length <= 2) return `Seleccionados: ${labels.join(', ')}`;
  return manyLabel(labels.length);
}

export type ShellSelectChipsTexts = {
  addPlaceholder: string;
  allAddedPlaceholder: string;
  emptyCatalog: string;
  emptySelection: string;
  chipsAriaLabel: string;
  noneSummary: string;
  manySummary: (count: number) => string;
};

export function shellSelectChipsField(opts: {
  label: string;
  id: string;
  error?: string;
  required?: boolean;
  items: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (values: string[]) => void;
  texts: ShellSelectChipsTexts;
}): TemplateResult {
  const selectedItems = opts.items.filter((item) => opts.selected.includes(item.value));
  const selectedLabels = selectedItems.map((item) => item.label);
  const summary = selectChipsSelectionSummary(selectedLabels, opts.texts.noneSummary, opts.texts.manySummary);
  const unselected = opts.items.filter((item) => !opts.selected.includes(item.value));
  const invalid = Boolean(opts.error);
  const required = opts.required ?? true;
  const selectId = `${opts.id}-add`;
  const selectClass = `${shellFieldInputClass(invalid)} bg-[color:var(--md-sys-color-surface-container-highest)]`;

  if (opts.items.length === 0) {
    return shellFieldBlock({
      label: opts.label,
      id: opts.id,
      error: opts.error,
      required,
      content: html`
        <p
          class="rounded-[var(--md-shape-corner-medium)] border border-dashed border-[color:var(--md-sys-color-outline-variant)] px-4 py-3 text-sm text-[color:var(--md-sys-color-on-surface-variant)]"
        >
          ${opts.texts.emptyCatalog}
        </p>
      `,
    });
  }

  return shellFieldBlock({
    label: opts.label,
    id: opts.id,
    error: opts.error,
    required,
    content: html`
      <div class="shell-select-chips-field space-y-3">
        <select
          id=${selectId}
          name=${selectId}
          class=${selectClass}
          ?disabled=${unselected.length === 0}
          aria-invalid=${invalid ? 'true' : 'false'}
          aria-describedby=${selectedLabels.length > 0 ? `${opts.id}-summary` : undefined}
          .value=""
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            if (!value) return;
            opts.onChange(addSelectChipValue(opts.selected, value));
            (e.target as HTMLSelectElement).value = '';
          }}
        >
          <option value="">
            ${unselected.length === 0 ? opts.texts.allAddedPlaceholder : opts.texts.addPlaceholder}
          </option>
          ${unselected.map((item) => html`<option value=${item.value}>${item.label}</option>`)}
        </select>

        ${selectedItems.length > 0
          ? html`
              <div class="flex flex-wrap gap-2" role="list" aria-label=${opts.texts.chipsAriaLabel}>
                ${selectedItems.map(
                  (item) => html`
                    <span
                      class="shell-select-chips-chip inline-flex min-h-[2.75rem] touch-manipulation items-center gap-2 rounded-full border border-[color:var(--md-sys-color-primary)] bg-[color:var(--md-sys-color-primary-container)] px-3 py-2 text-sm text-[color:var(--md-sys-color-on-primary-container)]"
                      role="listitem"
                    >
                      <span aria-hidden="true">✓</span>
                      ${item.label}
                      <button
                        type="button"
                        class="shell-select-chips-chip__remove ml-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-base leading-none transition-colors hover:bg-[color:color-mix(in_srgb,var(--md-sys-color-on-primary-container)_12%,transparent)]"
                        aria-label=${`Quitar ${item.label}`}
                        @click=${() => opts.onChange(removeSelectChipValue(opts.selected, item.value))}
                      >
                        ×
                      </button>
                    </span>
                  `,
                )}
              </div>
              <p
                id="${opts.id}-summary"
                class="text-sm font-medium text-[color:var(--md-sys-color-on-surface)]"
                aria-live="polite"
              >
                ${summary}
              </p>
            `
          : html`<p class="md-field__hint">${opts.texts.emptySelection}</p>`}
      </div>
    `,
  });
}

export function shellTextField(opts: {
  label: string;
  id: string;
  value: string;
  error?: string;
  type?: string;
  min?: string;
  max?: string;
  step?: string;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  inputmode?: string;
  placeholder?: string;
  sanitizeInput?: (value: string) => string;
  onInput?: (value: string) => void;
}): TemplateResult {
  const invalid = Boolean(opts.error);
  const required = opts.required ?? true;
  return html`
    <div class="md-field md-field--stacked ${invalid ? 'md-field--invalid' : ''}">
      ${shellFieldLabel(opts.label, opts.id, required)}
      <input
        id=${opts.id}
        name=${opts.id}
        type=${opts.type ?? 'text'}
        min=${opts.min}
        max=${opts.max}
        step=${opts.step}
        inputmode=${opts.inputmode || undefined}
        placeholder=${opts.placeholder || undefined}
        class=${shellFieldInputClass(invalid)}
        ?readonly=${opts.readonly ?? false}
        ?disabled=${opts.disabled ?? false}
        ?required=${required}
        aria-invalid=${invalid ? 'true' : 'false'}
        aria-describedby=${invalid ? `${opts.id}-error` : undefined}
        .value=${opts.value}
        @input=${opts.onInput
          ? (e: Event) => {
              const el = e.target as HTMLInputElement;
              const next = opts.sanitizeInput ? opts.sanitizeInput(el.value) : el.value;
              if (next !== el.value) el.value = next;
              opts.onInput!(next);
            }
          : undefined}
      />
      ${shellFieldHint(opts.id, opts.error)}
    </div>
  `;
}

export function shellFieldBlock(opts: {
  label: string;
  id: string;
  error?: string;
  required?: boolean;
  content: TemplateResult;
}): TemplateResult {
  const invalid = Boolean(opts.error);
  const required = opts.required ?? true;
  return html`
    <div id=${opts.id} class="md-field-block md-field--stacked ${invalid ? 'md-field--invalid' : ''}" aria-invalid=${invalid ? 'true' : 'false'}>
      ${shellBlockLabel(opts.label, required)}
      ${opts.content}
      ${shellFieldHint(opts.id, opts.error)}
    </div>
  `;
}

export function shellFriendlyAlert(message: string, tone: 'warning' | 'error'): TemplateResult {
  if (!message) return html``;
  const styles = {
    warning:
      'border-[color:var(--md-sys-color-tertiary)] bg-[color:var(--md-sys-color-tertiary-container)] text-[color:var(--md-sys-color-on-tertiary-container)]',
    error: 'md-error-banner border-[color:var(--md-sys-color-error)] bg-[color:var(--md-sys-color-error-container)] text-[color:var(--md-sys-color-on-error-container)]',
  };
  return html`
    <p class="rounded-[var(--md-shape-corner-medium)] border px-4 py-3 text-sm ${styles[tone]}" role="status">${message}</p>
  `;
}

export function shellSubmitButton(label: string, busyLabel: string, busy: boolean): TemplateResult {
  return html`
    <button
      type="submit"
      class="md-btn-filled md-sys-ripple touch-manipulation w-full sm:flex-1 ${busy ? 'md-btn-filled--busy' : ''}"
      ?disabled=${busy}
      aria-busy=${busy ? 'true' : 'false'}
    >
      ${busy ? busyLabel : label}
    </button>
  `;
}

export type ShellFormDataField = {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  required?: boolean;
  autocomplete?: string;
  inputmode?: string;
  enterkeyhint?: string;
};

export function shellFormDataField(opts: ShellFormDataField): TemplateResult {
  const required = opts.required !== false;
  return html`
    <div class="md-field md-field--stacked">
      ${shellFieldLabel(opts.label, opts.name, required)}
      <input
        id=${opts.name}
        name=${opts.name}
        type=${opts.type}
        placeholder=${opts.placeholder ?? ''}
        ?required=${required}
        autocomplete=${opts.autocomplete || 'off'}
        inputmode=${opts.inputmode || undefined}
        enterkeyhint=${opts.enterkeyhint || undefined}
        class="${shellFieldInputClass(false)} focus:ring-2"
      />
    </div>
  `;
}

export function shellFormDataForm(opts: {
  fields: ShellFormDataField[];
  submitLabel: string;
  submitBusy?: boolean;
  submitButtonClass?: string;
  onSubmit: (formData: FormData) => void | Promise<void>;
}): TemplateResult {
  const busy = Boolean(opts.submitBusy);
  const btnClass = opts.submitButtonClass ?? 'md-btn-filled md-sys-ripple w-full';
  return html`
    <form
      class="space-y-5"
      novalidate
      @submit=${async (e: Event) => {
        e.preventDefault();
        if (busy) return;
        const form = e.target as HTMLFormElement;
        try {
          await Promise.resolve(opts.onSubmit(new FormData(form)));
        } catch {
          /* Caller handles errors (e.g. login executeWithLoading). */
        }
      }}
    >
      ${opts.fields.map((f) => shellFormDataField(f))}
      <button
        type="submit"
        class="${btnClass} md-sys-ripple touch-manipulation ${busy ? 'md-btn-filled--busy' : ''}"
        ?disabled=${busy}
        aria-busy=${busy ? 'true' : 'false'}
      >
        ${opts.submitLabel}
      </button>
    </form>
  `;
}

export function shellBeneficiarioLookupField<T extends { documento: string; nombre: string }>(opts: {
  documento: string;
  nombre: string;
  docError?: string;
  results: T[];
  showResults: boolean;
  onQuery: (value: string) => void;
  onPick: (row: T) => void;
}): TemplateResult {
  return html`
    <div class="space-y-2">
      ${shellTextField({
        label: 'Documento o nombre del beneficiario',
        id: 'beneficiario-doc',
        value: opts.documento,
        error: opts.docError,
        onInput: opts.onQuery,
      })}
      ${opts.showResults && opts.results.length > 0
        ? html`
            <ul
              class="max-h-48 w-full overflow-auto rounded-[var(--md-shape-corner-medium)] border border-[color:var(--md-sys-color-outline-variant)] bg-[color:var(--md-sys-color-surface-container-highest)] shadow-md"
              role="listbox"
            >
              ${opts.results.map(
                (row) => html`
                  <li>
                    <button
                      type="button"
                      class="block w-full px-4 py-3 text-left text-sm transition-colors hover:bg-[color:var(--md-sys-color-surface-container-high)]"
                      role="option"
                      @click=${() => opts.onPick(row)}
                    >
                      ${row.documento} — ${row.nombre}
                    </button>
                  </li>
                `,
              )}
            </ul>
          `
        : opts.showResults
          ? html`<p class="text-sm text-[color:var(--md-sys-color-on-surface-variant)]">Sin coincidencias.</p>`
          : null}
    </div>
    ${shellTextField({
      label: 'Nombre beneficiario',
      id: 'beneficiario-nombre',
      value: opts.nombre,
      readonly: true,
      required: false,
    })}
  `;
}
